import { graphql } from "./generated/index.js"

// Queries ======================================================================

export const fetchRecordListings = graphql(`
  query fetchRecordListings($listId: Int!) {
    listings: record_listings(
      where: { list_id: { _eq: $listId } }
      order_by: [{ position: asc }, { created_at: desc }]
    ) {
      ...RecordListingFields
      list {
        id
        visibility
        owner_person_id
        owner_org_id
        data
      }
      record {
        ...RecordFields
        taggings {
          ...RecordTaggingFields
        }
        contacts {
          ...RecordContactFields
        }
        sources {
          ...RecordSourceFields
        }
        attachments {
          ...RecordAttachmentFields
        }
        events {
          ...RecordFields
        }
        organizations {
          ...RecordFields
        }
        people {
          ...RecordFields
        }
        links {
          ...RecordLinkFields
        }
      }
    }
  }
`)

export const fetchOpenRecordListings = graphql(`
  query fetchOpenRecordListings($listId: Int!) {
    listings: record_listings(
      where: { list_id: { _eq: $listId }, record: { data: { _contains: { deal_status: "open" } } } }
      order_by: [{ position: asc }, { created_at: desc }]
    ) {
      ...RecordListingFields
      list {
        id
        visibility
        owner_person_id
        owner_org_id
        data
      }
      record {
        ...RecordFields
        taggings {
          ...RecordTaggingFields
        }
        contacts {
          ...RecordContactFields
        }
        sources {
          ...RecordSourceFields
        }
        attachments {
          ...RecordAttachmentFields
        }
        events {
          ...RecordFields
        }
        organizations {
          ...RecordFields
        }
        people {
          ...RecordFields
        }
        links {
          ...RecordLinkFields
        }
      }
    }
  }
`)

// The list record's data — the guard resolves data.listing_schema_id from it
// to validate context keys on the INSERT door (add/addMany), where no listing
// row exists to pre-read yet. Fetched only when a context bag has keys.
export const fetchListContextSchema = graphql(`
  query fetchListContextSchema($id: Int!) {
    list: records_by_pk(id: $id) {
      id
      data
    }
  }
`)

// The list's current max position — the append base for add/addMany when the
// caller supplies none.
export const fetchListMaxPosition = graphql(`
  query fetchListMaxPosition($listId: Int!) {
    record_listings_aggregate(where: { list_id: { _eq: $listId } }) {
      aggregate {
        count
        max {
          position
        }
      }
    }
  }
`)

// A single listing's current row — read before a partial update, so
// computeListingChanges can diff against it and merge into its `context`
// rather than overwrite it.
export const fetchRecordListingById = graphql(`
  query fetchRecordListingById($id: Int!) {
    listing: record_listings_by_pk(id: $id) {
      ...RecordListingFields
      list {
        data
      }
    }
  }
`)

// Batch read for updateMany/reorder — one round trip for every current row a
// batch update needs to diff against, instead of one per id.
export const fetchRecordListingsByIds = graphql(`
  query fetchRecordListingsByIds($ids: [Int!]!) {
    listings: record_listings(where: { id: { _in: $ids } }) {
      ...RecordListingFields
      list {
        data
      }
    }
  }
`)

// Every membership row of a list, in list order — the source side of `copy`.
export const fetchListingsToCopy = graphql(`
  query fetchListingsToCopy($listId: Int!) {
    listings: record_listings(
      where: { list_id: { _eq: $listId } }
      order_by: [{ position: asc_nulls_last }, { created_at: asc }]
    ) {
      record_id
      label
      position
      context
    }
  }
`)

// Mutations ====================================================================

export const addRecordListing = graphql(`
  mutation addRecordListing(
    $listId: Int!
    $recordId: Int!
    $position: Int
    $label: String
    $context: jsonb
  ) {
    insert_record_listings_one(
      object: {
        list_id: $listId
        record_id: $recordId
        position: $position
        label: $label
        context: $context
      }
      on_conflict: { constraint: record_listings_list_id_record_id_unique, update_columns: [] }
    ) {
      ...RecordListingFields
    }
  }
`)

export const removeRecordListing = graphql(`
  mutation removeRecordListing($id: Int!) {
    delete_record_listings_by_pk(id: $id) {
      id
      record_id
      list_id
    }
  }
`)

export const removeMultipleRecordListings = graphql(`
  mutation removeMultipleRecordListings($ids: [Int!]!) {
    delete_record_listings(where: { id: { _in: $ids } }) {
      affected_rows
      returning {
        id
        record_id
        list_id
      }
    }
  }
`)

export const updateRecordListing = graphql(`
  mutation updateRecordListing($id: Int!, $_set: record_listings_set_input!) {
    update_record_listings_by_pk(pk_columns: { id: $id }, _set: $_set) {
      ...RecordListingFields
    }
  }
`)

// Batch update — one update_record_listings_many call, one {where, _set} entry
// per input. The response is positional: updates[i] ↔ update_record_listings_many[i],
// each carrying `affected_rows` (0 ⇒ no row matched — missing / RLS-hidden /
// constraint-failed) and `returning` (the updated row). reorder() reuses this
// with position-only _set entries.
export const updateManyRecordListings = graphql(`
  mutation updateManyRecordListings($updates: [record_listings_updates!]!) {
    update_record_listings_many(updates: $updates) {
      affected_rows
      returning {
        ...RecordListingFields
      }
    }
  }
`)

// ON CONFLICT DO NOTHING on the (list_id, record_id) natural key — adding a
// record already on the list is idempotent, not an error. `returning`
// includes record_id so the caller can split "newly added" from
// "already listed" (rows already present don't appear in `returning`).
export const createManyRecordListings = graphql(`
  mutation createManyRecordListings($objects: [record_listings_insert_input!]!) {
    insert_record_listings(
      objects: $objects
      on_conflict: { constraint: record_listings_list_id_record_id_unique, update_columns: [] }
    ) {
      affected_rows
      returning {
        ...RecordListingFields
      }
    }
  }
`)

// The list record under `parentId` whose data holds the given role/record_type
// match object — passed as ONE jsonb variable so `_contains` takes it whole.
// Archived lists are skipped; lowest id first when several match.
export const fetchListByParentAndData = graphql(`
  query fetchListByParentAndData($parentId: Int!, $match: jsonb!) {
    lists: records(
      where: {
        type: { _eq: "list" }
        parent_id: { _eq: $parentId }
        archived_at: { _is_null: true }
        data: { _contains: $match }
      }
      order_by: { id: asc }
      limit: 1
    ) {
      id
      name
      slug
      parent_id
      data
    }
  }
`)
